// The app's single QueryClient, plus the rule that ties its lifetime to the
// logged-in account.
//
// Almost every cached response is scoped to the caller by the BE rather than
// by the query key: /ekip/schedule computes isMine/isPhoto/myAccept for the
// requesting staff, /tasks/me and /payroll/me are per-user by definition, and
// the dashboard endpoints filter by branch scope. The keys are the same for
// everyone ("ekip", "schedule"), so after switching accounts on the same
// device React Query would serve the previous person's payload until it went
// stale — 30-60s of a makeup account looking at a photographer's buttons, and
// of upload slots hidden because isMine was false. Reloading the page "fixed"
// it only because that built a new client.
//
// Clearing on identity change is the fix. Doing it here, keyed off the store,
// covers every entry point (PIN login, add-account, logout, an expired session
// clearing itself) instead of asking each of them to remember.

import { QueryClient } from "@tanstack/react-query";
import { useAuthV2 } from "@/lib/authv2/store";

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
});

/**
 * Drop every cached response when the signed-in account changes.
 *
 * `resetQueries`, not `clear`. Verified against @tanstack/react-query v5:
 * `clear()` empties the cache but leaves a *mounted* observer holding — and
 * rendering — its last data, with no refetch triggered. The dashboard survives
 * an account switch without unmounting, so `clear()` alone left the exact
 * screen this fixes still showing the previous account's payload.
 * `resetQueries()` discards inactive entries and refetches the active ones, and
 * a slow in-flight request from the previous account no longer lands (its query
 * was already detached), so it cannot overwrite the new account's data.
 *
 * Returns the zustand unsubscribe so tests can detach; production never does.
 * Transitions to null (logout) reset too — the next account must not inherit
 * what the previous one left behind.
 */
export function bindQueryCacheToSession(client: QueryClient = queryClient): () => void {
  let previousSub = useAuthV2.getState().casdoorSub;
  return useAuthV2.subscribe((state) => {
    const nextSub = state.casdoorSub;
    if (nextSub === previousSub) return;
    previousSub = nextSub;
    void client.resetQueries();
  });
}
